import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mx-auto mb-4">
              <i className="ri-file-unknow-line text-2xl"></i>
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-foreground-600 mb-6">您访问的文章可能已被移除或链接有误</p>
            <Link to="/" className="px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors inline-block whitespace-nowrap">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug)
    .filter((a) => a.category === article.category || a.tags.some((t) => article.tags.includes(t)))
    .slice(0, 3);

  const siteUrl = 'https://dapigumanhua.com';
  const articleUrl = `${siteUrl}/article/${article.slug}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `${siteUrl}/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title, item: articleUrl },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.image,
        datePublished: article.date,
        dateModified: article.date,
        author: { '@type': 'Person', name: article.author },
        publisher: { '@type': 'Organization', name: '打屁股漫画大全', url: siteUrl },
        url: articleUrl,
        mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 打屁股漫画大全`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={articleUrl}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.date}
        articleModifiedTime={article.date}
      />
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <Navbar />

      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors">
              {category?.name}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium truncate max-w-[200px]">{article.title}</span>
          </nav>
        </div>

        {/* Article Header */}
        <section className="bg-background-100 py-10">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <Link
                to={`/category/${article.category}`}
                className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {category?.name}
              </Link>
              {article.tags.map((tag) => (
                <span key={tag} className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {(article.readCount / 1000).toFixed(1)}k 阅读
              </span>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-10 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            {/* Featured Image */}
            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Content */}
            <div className="prose-custom">
              {article.content.map((paragraph, idx) => (
                <p key={idx} className="text-foreground-700 leading-relaxed mb-5 text-[15px]">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Tags Footer */}
            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <section className="py-12 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedArticles.map((related) => (
                <Link
                  key={related.slug}
                  to={`/article/${related.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={related.image}
                      alt={related.title}
                      title={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <span className="inline-block px-2 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-full mb-2">
                      {categories.find((c) => c.slug === related.category)?.name}
                    </span>
                    <h3 className="font-heading font-bold text-foreground-900 text-sm mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {related.title}
                    </h3>
                    <span className="text-xs text-foreground-500">{(related.readCount / 1000).toFixed(1)}k 阅读</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}